/**
 * Guía: Comercios recomendados (Bono Pionero).
 *
 * Es la tercera configuración del mismo componente que «Solicitudes» y
 * «Solicitudes de asesores», así que el embudo, la asignación y las notas no se
 * vuelven a explicar: la guía de solicitudes ya lo hace y se dice dónde.
 *
 * Lo que sí hay que contar es lo que esta pantalla tiene y las otras dos no, y
 * son las tres cosas que pueden costar dinero o una discusión con un cliente:
 * detrás de cada fila hay **una persona esperando un bono**, los importes son
 * **los que se le prometieron** y no los de ajustes, y las señales de duplicado
 * son **avisos, no veredictos**.
 */

import type { TourFlow } from '../types';

const RECOMMENDATIONS_PATH = '/recommendations';

export const c09bComerciosRecomendados: TourFlow = {
  id: 'admin.recommendations',
  chapter: '09b',
  title: 'Comercios recomendados',
  summary:
    'La cola del Bono Pionero: quién cobra, cuánto se le prometió y por qué un duplicado no se rechaza solo.',
  href: RECOMMENDATIONS_PATH,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'Comercios que piden los usuarios',
      body:
        'Son negocios que todavía no están en 121Pass y que un cliente quiere encontrar ' +
        'aquí. A diferencia de las solicitudes, el comercio no pidió nada: lo propuso ' +
        'alguien de fuera. El usuario recomienda; 121Pass decide.',
      route: RECOMMENDATIONS_PATH,
    },
    {
      id: 'someone-is-waiting',
      title: 'Detrás de cada fila hay alguien esperando un bono',
      body:
        'Si el comercio acaba activo en la plataforma, quien lo recomendó cobra en su ' +
        'billetera: una parte al activarse y otra si el comercio aguanta los días de ' +
        'permanencia. Por eso conviene mirar quién la mandó antes de descartarla.',
      // La tabla es el componente genérico compartido con las otras dos
      // bandejas, así que el marcador también lo es — igual que hace la
      // guía de solicitudes de asesores.
      target: 'admin.leads.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'frozen-amounts',
      title: 'Los importes son los que se le prometieron',
      body:
        'La ficha enseña lo prometido a ESA recomendación, congelado el día que se envió. ' +
        'Si alguien cambia el bono en Parámetros, las que ya estaban en curso siguen ' +
        'valiendo lo suyo. Un importe que no cuadra con ajustes no es un error: es eso.',
    },
    {
      id: 'duplicates-are-a-warning',
      title: 'Duplicada y «empresa parecida» son avisos, no veredictos',
      body:
        'Si otra recomendación llegó antes por el mismo comercio, la atribución es de la ' +
        'primera y ésta no genera bono. Y si el sistema encontró una empresa de nombre ' +
        'parecido, es un parecido de texto: puede ser el mismo negocio o no. Comprobarlo ' +
        'es trabajo de una persona, por eso no se rechaza solo.',
    },
    {
      id: 'convert-then-activate',
      title: 'Vincular y activar son dos momentos',
      body:
        'Vincularla a una empresa no paga nada: el primer tramo se libera cuando esa ' +
        'empresa queda activa, normalmente al aprobarla. El botón de confirmar activación ' +
        'está para los casos raros, y es inofensivo: si ya se pagó, lo dice y no paga otra vez.',
    },
    {
      id: 'same-as-leads',
      title: 'El resto funciona como Solicitudes',
      body:
        'Mismo embudo de estados, misma asignación, mismas notas. Lo que aprendiste en la ' +
        'guía de Solicitudes vale aquí; lo de arriba es lo único propio de esta pantalla.',
    },
  ],
};
